import { Check, ChevronDown } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import "./game-select.css";

export interface GameSelectOption<Value extends string> {
  value: Value;
  label: string;
  description?: string;
}

interface GameSelectProps<Value extends string> {
  value: Value;
  onChange: (value: Value) => void;
  options: readonly GameSelectOption<Value>[];
  label: string;
  id?: string;
  disabled?: boolean;
  compact?: boolean;
  className?: string;
}

/** Select-only combobox: focus stays on the trigger while the active option moves. */
export function GameSelect<Value extends string>({
  value,
  onChange,
  options,
  label,
  id,
  disabled = false,
  compact = false,
  className = "",
}: GameSelectProps<Value>) {
  const generatedId = useId();
  const triggerId = id ?? `game-select-${generatedId}`;
  const listId = `${triggerId}-options`;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const activeRef = useRef<HTMLLIElement>(null);
  const searchRef = useRef({ text: "", time: 0 });
  const [open, setOpen] = useState(false);
  const [popup, setPopup] = useState({ above: false, height: 330 });
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  const expanded = open && !disabled && options.length > 0;
  const activeOptionId = `${listId}-${activeIndex}`;

  useEffect(() => {
    if (!expanded) return;
    const outside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    const resize = () => setOpen(false);
    window.addEventListener("resize", resize);
    return () => {
      document.removeEventListener("pointerdown", outside);
      window.removeEventListener("resize", resize);
    };
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    const option = activeRef.current;
    const list = option?.parentElement;
    if (!option || !list) return;
    if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop;
    else if (
      option.offsetTop + option.offsetHeight >
      list.scrollTop + list.clientHeight
    )
      list.scrollTop =
        option.offsetTop + option.offsetHeight - list.clientHeight;
  }, [expanded, activeIndex]);

  function openPopup(index: number) {
    const bounds = triggerRef.current?.getBoundingClientRect();
    if (bounds) {
      const container = triggerRef.current
        ?.closest(".worldMap")
        ?.getBoundingClientRect();
      const below =
        Math.min(window.innerHeight, container?.bottom ?? window.innerHeight) -
        bounds.bottom -
        12;
      const above = bounds.top - Math.max(0, container?.top ?? 0) - 12;
      const desired = Math.min(330, window.innerHeight * 0.42);
      const opensAbove = below < desired && above > below;
      setPopup({
        above: opensAbove,
        height: Math.max(44, Math.min(desired, opensAbove ? above : below)),
      });
    }
    setActiveIndex(index);
    setOpen(true);
  }

  function choose(index: number) {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    searchRef.current = { text: "", time: 0 };
    setOpen(false);
    triggerRef.current?.focus({ preventScroll: true });
  }

  function handleKey(event: KeyboardEvent<HTMLButtonElement>) {
    const last = options.length - 1;
    if (last < 0) return;
    if (event.key === "Escape" && expanded) {
      event.preventDefault();
      event.stopPropagation();
      searchRef.current = { text: "", time: 0 };
      setOpen(false);
      return;
    }
    if (event.key === "Tab") {
      // Preserve the focused DOM until native traversal completes; blur closes it.
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (expanded) choose(activeIndex);
      else {
        openPopup(selectedIndex);
      }
      return;
    }
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      openPopup(
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? last
            : !expanded
              ? selectedIndex
              : Math.max(
                  0,
                  Math.min(
                    last,
                    activeIndex + (event.key === "ArrowDown" ? 1 : -1),
                  ),
                ),
      );
      return;
    }
    if (
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey
    ) {
      event.preventDefault();
      const now = Date.now();
      const previous = searchRef.current;
      const character = event.key.toLocaleLowerCase();
      const search =
        now - previous.time < 700 ? previous.text + character : character;
      const repeated = [...search].every((letter) => letter === character);
      const prefix = repeated ? character : search;
      searchRef.current = { text: search, time: now };
      const start = expanded ? activeIndex : selectedIndex;
      // Extending a prefix refines the current match; repeated letters cycle.
      const firstOffset = !repeated && expanded ? 0 : 1;
      for (
        let offset = firstOffset;
        offset < firstOffset + options.length;
        offset += 1
      ) {
        const index = (start + offset) % options.length;
        if (options[index].label.toLocaleLowerCase().startsWith(prefix)) {
          openPopup(index);
          break;
        }
      }
    }
  }

  return (
    <div
      ref={rootRef}
      className={`gameSelect ${compact ? "gameSelect--compact" : ""} ${className}`}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        role="combobox"
        aria-label={label}
        aria-expanded={expanded}
        aria-haspopup="listbox"
        aria-controls={expanded ? listId : undefined}
        aria-activedescendant={expanded ? activeOptionId : undefined}
        disabled={disabled || options.length === 0}
        className="gameSelect__trigger"
        onKeyDown={handleKey}
        onClick={() => {
          if (expanded) setOpen(false);
          else openPopup(selectedIndex);
        }}
      >
        <span>
          {options.find((option) => option.value === value)?.label ?? "Select"}
        </span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {expanded ? (
        <ul
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-label={label}
          className="gameSelect__options"
          style={{
            maxHeight: popup.height,
            ...(popup.above ? { top: "auto", bottom: "calc(100% + 5px)" } : {}),
          }}
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              ref={index === activeIndex ? activeRef : undefined}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={option.value === value}
              tabIndex={-1}
              className={`gameSelect__option ${index === activeIndex ? "is-active" : ""}`}
              onPointerMove={(event) => {
                if (event.pointerType === "mouse") setActiveIndex(index);
              }}
              onPointerDown={(event) => {
                if (event.pointerType === "mouse") event.preventDefault();
              }}
              onClick={() => choose(index)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  choose(index);
                }
              }}
            >
              <span>
                <strong>{option.label}</strong>
                {option.description ? (
                  <small>{option.description}</small>
                ) : null}
              </span>
              {option.value === value ? (
                <Check size={14} aria-hidden="true" />
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
